import React from 'react';
import AtlasV2ComparableSplineChart from 'src/components/AtlasV2Components/AtlasV2graphs/AtlasV2ComparableSplineChart';
import AtlasV2UnitsSold1P3PSplineChart from 'src/components/AtlasV2Components/AtlasV2UnitsSoldPageComponents/AtlasV2UnitsSold1P3PSplineChart';
import AtlasV2UnitsSold1PTotalUnitsSoldSplineChart from 'src/components/AtlasV2Components/AtlasV2UnitsSoldPageComponents/AtlasV2UnitsSold1PTotalUnitsSoldSplineChart';
import AtlasV2UnitsSoldDoubleDropdownTopEntityChart from 'src/components/AtlasV2Components/AtlasV2UnitsSoldPageComponents/AtlasV2UnitsSoldDoubleDropdownTopEntityChart';
import AtlasV2UnitsSoldDropdownTopEntityChart from 'src/components/AtlasV2Components/AtlasV2UnitsSoldPageComponents/AtlasV2UnitsSoldDropdownTopEntityChart';
import AtlasV2UnitsSoldMarketShareOfMultipleSelectSplineChart from 'src/components/AtlasV2Components/AtlasV2UnitsSoldPageComponents/AtlasV2UnitsSoldMarketShareOfMultipleSelectSplineChart';
import AtlasV2UnitsSoldMarketShareSplineChart from 'src/components/AtlasV2Components/AtlasV2UnitsSoldPageComponents/AtlasV2UnitsSoldMarketShareSplineChart';
import AtlasV2UnitsSoldProductGrid from 'src/components/AtlasV2Components/AtlasV2UnitsSoldPageComponents/AtlasV2UnitsSoldProductGrid';
import AtlasV2SalesProductCountSplineChart from 'src/components/AtlasV2Components/AtlasV2SalesPageComponents/AtlasV2SalesProductCountSplineChart';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { BEACON_CHART_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { shouldShowOnePRetailSales } from 'src/utils/app';
import {
  useBrandEntity,
  useBusinessUnitEntity,
  useCategoryEntity,
  useMainEntityType,
  useProductEntity,
  useSegmentEntity,
  useSubcategoryEntity
} from 'src/utils/Hooks/reduxSelectors';
import AtlasV2UnitsSoldDoubleDropdownCategoryMarketShareChart from 'src/components/AtlasV2Components/AtlasV2UnitsSoldPageComponents/AtlasV2UnitsSoldDoubleDropdownCategoryMarketShareChart';
import AtlasV2UnitsSoldMarketShareGrid from 'src/components/AtlasV2Components/AtlasV2UnitsSoldPageComponents/AtlasV2UnitsSoldMarketShareGrid';
import AtlasV2UnitsSoldMarketShareProductSplineChart from 'src/components/AtlasV2Components/AtlasV2UnitsSoldPageComponents/AtlasV2UnitsSoldMarketShareProductSplineChart';
import AtlasV2UnitsSoldMarketShareDropdownTopEntityChart from 'src/components/AtlasV2Components/AtlasV2UnitsSoldPageComponents/AtlasV2UnitsSoldMarketShareDropdownTopEntityChart';
import { useAppSelector } from 'src/utils/Hooks';
import { ONE_P_THRESHOLD } from 'src/components/AtlasV2Components/AtlasV2PageLayouts/AtlasV2SalesSummaryPageLayout';
import { useAtlasV2TabTreatmentRedirect } from 'src/components/AtlasV2Components/Hooks/useAtlasV2TabTreatmentRedirect';

/**
 * Atlas V2 units sold Page layout - Tab name: sales subtab name: units sold
 */
export const AtlasV2SalesUnitsSoldPageLayout = () => {
  const mainEntityType = useMainEntityType();
  const { isProductEntity } = useProductEntity();
  const { isCategoryEntity } = useCategoryEntity();
  const { isSubcategoryEntity } = useSubcategoryEntity();
  const { isBrandEntity } = useBrandEntity();
  const { isSegmentEntity } = useSegmentEntity();
  const { isBusinessUnitEntity } = useBusinessUnitEntity();
  const skuCount = useAppSelector((state) => state.entityService.mainEntity.skuCount);
  const { isRedirecting } = useAtlasV2TabTreatmentRedirect();
  if (isRedirecting) return null;

  // for segment, if sku count is less than or equal to 25, hide the one P, three P spline chart
  return shouldShowOnePRetailSales() && !(isSegmentEntity && skuCount <= ONE_P_THRESHOLD) ? (
    <BasePageContainer>
      <Flex flexDirection="column" gap={BEACON_CHART_SPACING}>
        {/* One P, Three P spline chart that will have split.io implemented in.  */}
        {!isProductEntity ? (
          <AtlasV2UnitsSold1P3PSplineChart />
        ) : (
          <AtlasV2ComparableSplineChart indexName="sales" fieldName="unitsSold" />
        )}

        {/* Market Share of Units Sold (One P, Three P) - split,io in */}
        {(mainEntityType === 'company' || isBrandEntity) && <AtlasV2UnitsSoldMarketShareOfMultipleSelectSplineChart />}

        {/* Product Count */}
        {!isProductEntity && <AtlasV2SalesProductCountSplineChart />}

        {/* Double dropdown top entity retail sales, one P, three P  */}
        {isCategoryEntity || isSubcategoryEntity || isSegmentEntity || isBusinessUnitEntity ? (
          <AtlasV2UnitsSoldDoubleDropdownCategoryMarketShareChart />
        ) : !isProductEntity && !isBrandEntity ? (
          <AtlasV2UnitsSoldDoubleDropdownTopEntityChart />
        ) : null}

        {/* Market Share Table */}
        {(isCategoryEntity || isSubcategoryEntity || isSegmentEntity || mainEntityType === 'businessunit') && (
          <AtlasV2UnitsSoldMarketShareGrid />
        )}

        {(isCategoryEntity || isSubcategoryEntity || isBrandEntity || isSegmentEntity || isBusinessUnitEntity) && (
          //  Double Dropdown Retail Sales, 1P, 3P by Brand Manufacturer
          <AtlasV2UnitsSoldDoubleDropdownTopEntityChart />
        )}

        {/* oneP Units Sold vs total */}
        {(isCategoryEntity ||
          isSubcategoryEntity ||
          isBrandEntity ||
          isSegmentEntity ||
          mainEntityType === 'businessunit' ||
          mainEntityType === 'company') && (
          //  Double Dropdown Retail Sales, 1P, 3P by Brand Manufacturer
          <AtlasV2UnitsSold1PTotalUnitsSoldSplineChart />
        )}

        {isProductEntity && <AtlasV2UnitsSoldMarketShareProductSplineChart />}

        {/* Product Grid */}
        <AtlasV2UnitsSoldProductGrid />
      </Flex>
    </BasePageContainer>
  ) : (
    <BasePageContainer>
      <Flex flexDirection="column" gap={BEACON_CHART_SPACING}>
        {/* Units Sold Chart  */}
        <AtlasV2ComparableSplineChart indexName="sales" fieldName="unitsSold" />
        {/* Market Share of units sold  */}
        {(mainEntityType === 'company' || isBrandEntity || isProductEntity) && (
          <AtlasV2UnitsSoldMarketShareSplineChart />
        )}

        {/* Product Count */}
        {!isProductEntity && <AtlasV2SalesProductCountSplineChart />}

        {!isCategoryEntity &&
          !isSubcategoryEntity &&
          !isBrandEntity &&
          !isProductEntity &&
          !isSegmentEntity &&
          !isBusinessUnitEntity && <AtlasV2UnitsSoldDropdownTopEntityChart />}

        {(isCategoryEntity || isSubcategoryEntity || isSegmentEntity || isBusinessUnitEntity) && (
          <AtlasV2UnitsSoldMarketShareDropdownTopEntityChart />
        )}

        {(isCategoryEntity || isSubcategoryEntity || isSegmentEntity || mainEntityType === 'businessunit') && (
          <AtlasV2UnitsSoldMarketShareGrid />
        )}

        {/* dropdown top entity units sold  */}
        {(isCategoryEntity || isSubcategoryEntity || isSegmentEntity || isBrandEntity || isBusinessUnitEntity) && (
          <AtlasV2UnitsSoldDropdownTopEntityChart />
        )}

        {/* Product Grid */}
        <AtlasV2UnitsSoldProductGrid />
      </Flex>
    </BasePageContainer>
  );
};
